import { useEffect, useState } from "react";
import { Shield } from "lucide-react";

import { manualAdminRoleOptions, type ManualAdminRoleCode } from "../../constants/userAdminRoles";
import type { ManagedUserRow } from "../../types/userManagement";
import { Button } from "../atoms/Button";
import { Drawer } from "./Drawer";

type UserAdminRolesDrawerProps = {
  readonly isOpen: boolean;
  readonly user: ManagedUserRow | null;
  /** When set and equal to `user.userId`, SUPER_ADMIN cannot be unchecked if already assigned. */
  readonly currentUserId?: string | null;
  readonly onClose: () => void;
  readonly onSave: (userId: string, adminRoles: ManualAdminRoleCode[]) => Promise<void>;
};

export const UserAdminRolesDrawer = ({
  isOpen,
  user,
  currentUserId,
  onClose,
  onSave
}: UserAdminRolesDrawerProps) => {
  const [selected, setSelected] = useState<Set<ManualAdminRoleCode>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !user) {
      return;
    }
    setError(null);
    const next = new Set<ManualAdminRoleCode>();
    for (const r of user.adminRoles) {
      const u = r.trim().toUpperCase();
      if (manualAdminRoleOptions.some((o) => o.code === u)) {
        next.add(u as ManualAdminRoleCode);
      }
    }
    setSelected(next);
  }, [isOpen, user]);

  const toggle = (code: ManualAdminRoleCode) => {
    setSelected((prev) => {
      const copy = new Set(prev);
      if (copy.has(code)) {
        copy.delete(code);
      } else {
        copy.add(code);
      }
      return copy;
    });
  };

  if (!user) {
    return null;
  }

  const isSelf =
    !!currentUserId &&
    currentUserId.trim().toLowerCase() === user.userId.trim().toLowerCase();
  const superAdminSelfLock =
    isSelf &&
    user.adminRoles.some((r) => r.trim().toUpperCase() === "SUPER_ADMIN");

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Edit admin roles"
      subtitle={user.email}
      headerIcon={<Shield className="h-5 w-5" />}
      panelClassName="max-w-lg"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Choose one or more application admin roles for <span className="font-medium text-gray-900">{user.userName}</span>.
          Changes are saved to the database as a JSON list of role codes.
        </p>
        {superAdminSelfLock ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            You cannot remove <span className="font-mono font-semibold">SUPER_ADMIN</span> from your own account while
            editing it here.
          </p>
        ) : null}
        <fieldset className="space-y-2">
          <legend className="sr-only">Admin roles</legend>
          {manualAdminRoleOptions.map(({ code, label }) => (
            <label
              key={code}
              className={`flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm hover:border-primary-200 ${
                code === "SUPER_ADMIN" && superAdminSelfLock ? "cursor-not-allowed opacity-90" : "cursor-pointer"
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300"
                disabled={code === "SUPER_ADMIN" && superAdminSelfLock}
                checked={selected.has(code)}
                onChange={() => {
                  if (code === "SUPER_ADMIN" && superAdminSelfLock) {
                    return;
                  }
                  toggle(code);
                }}
              />
              <span>
                <span className="font-medium text-gray-900">{label}</span>
                <span className="mt-0.5 block font-mono text-xs text-gray-500">{code}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={saving}
            onClick={async () => {
              setError(null);
              setSaving(true);
              try {
                await onSave(user.userId, [...selected]);
                onClose();
              } catch (err) {
                setError(err instanceof Error ? err.message : "Unable to save roles.");
              } finally {
                setSaving(false);
              }
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
